import {useQuery} from '@github-ui/react-query'
import {useMemo} from 'react'

import {savedViewsQueryOptions} from '../queries'
import type {SavedView, SavedViewsResponse, SavedViewVisibility} from '../types'
import {viewVisibility} from '../utils/views-visibility'

const EMPTY_SAVED_VIEWS: SavedView[] = []

export interface UsePinnedSavedViewsResult {
  pinnedViews: SavedView[]
  pinnedCount: number | undefined
  pinnedCountsByVisibility: Record<SavedViewVisibility, number> | undefined
  isFetching: boolean
}

export function usePinnedSavedViews({
  owner,
  repo,
  enabled,
  initialData,
}: {
  owner: string
  repo: string
  enabled: boolean
  initialData?: SavedViewsResponse
}): UsePinnedSavedViewsResult {
  const {data, isFetching} = useQuery({...savedViewsQueryOptions({owner, repo, initialData}), enabled})
  const savedViews = data?.savedViews || EMPTY_SAVED_VIEWS
  const allPinnedViews = useMemo(() => savedViews.filter(view => view.pinned), [savedViews])
  const pinnedViews = useMemo(() => (enabled ? allPinnedViews : []), [allPinnedViews, enabled])
  const pinnedCount = data ? allPinnedViews.length : undefined
  const pinnedCountsByVisibility = useMemo(
    () =>
      data
        ? allPinnedViews.reduce<Record<SavedViewVisibility, number>>(
            (counts, view) => {
              counts[viewVisibility(view)] += 1
              return counts
            },
            {team: 0, personal: 0},
          )
        : undefined,
    [allPinnedViews, data],
  )

  return {pinnedViews, pinnedCount, pinnedCountsByVisibility, isFetching}
}
